import { ExportOutlined } from '@ant-design/icons';
import { Col, Collapse, Row, Typography } from 'antd';
import { motion } from 'framer-motion';
import Image from 'next/image';

const { Panel } = Collapse;

const { Title, Paragraph, Link } = Typography;

function VMCPrepare() {
  return (
    <div>
      <motion.div
        className="text-center mg-bottom-48px"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="mg-bottom-8px">
          <div className="text-100 bold color-accent-1 text-uppercase">
            Elevate Your Emails with VMC
          </div>
        </div>
        <h2 className="display-2 color-neutral-100">
          Preparing Your Business for <span className="text-no-wrap">VMC Implementation</span>
        </h2>
        <p className="color-neutral-200">
          From initial setup to final verification, we&apos;re here to help you master the
          essentials of verified mark certificate implementation.
        </p>
      </motion.div>

      <Row gutter={24} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Col xs={{ span: 24 }} md={{ span: 12 }} style={{ marginBottom: '24px' }}>
          <motion.div
            className="card testimonial-slider"
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="card-testimonial-slider-content">
              <div className="card-testimonial-slider-details mg-bottom-24px">
                <div className="text-400 medium color-neutral-100">
                  <Image
                    src="/images/icon-blue-check.svg"
                    loading="eager"
                    alt="Activate DMARC Compliance Now"
                    height={32}
                    width={32}
                  />{' '}
                  Activate DMARC Compliance Now
                </div>
                <div className="text-200 medium color-accent-1">
                  Prioritize email integrity and build trust
                </div>
              </div>
              <p className="color-neutral-300">
                To obtain a Verified Mark Certificate (VMC), it&apos;s crucial that your
                organization first achieves DMARC compliance. Determine if your email domain already
                employs DMARC through our <Link href="/dmarc-inspector/">DMARC Inspector </Link>.
                Establishing DMARC requires careful consideration and time. Begin your journey with
                our insightful{' '}
                <Link
                  href="https://www.digicert.com/content/dam/digicert/pdfs/guide/how-to-setup-dmarc-guide-en.pdf"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  DMARC step-by-step guide <ExportOutlined />{' '}
                </Link>
                .
              </p>
            </div>
          </motion.div>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 12 }} style={{ marginBottom: '24px' }}>
          <motion.div
            className="card testimonial-slider"
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="card-testimonial-slider-content">
              <div className="card-testimonial-slider-details mg-bottom-24px">
                <div className="text-400 medium color-neutral-100">
                  <Image
                    src="/images/icon-blue-check.svg"
                    loading="eager"
                    alt="Secure Your Logo's Trademark Registration"
                    height={32}
                    width={32}
                  />{' '}
                  Secure Your Logo&apos;s Trademark Registration
                </div>
                <div className="text-200 medium color-accent-1">
                  Ensure exclusive rights and recognition for your brand
                </div>
              </div>
              <p className="color-neutral-300">
                Before securing the high-assurance VMC, a Registered Mark Certificate, you must
                ensure your brand&apos;s logo is registered with the relevant{' '}
                <Link
                  href="https://www.digicert.com/blog/qualify-for-a-vmc-how-to-trademark-your-logo"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  government trademark office <ExportOutlined />{' '}
                </Link>{' '}
                in your locality. Acquiring a trademark can be a prolonged affair; it&apos;s
                advisable to initiate the process early. For more on the trademark registration,
                explore{' '}
                <Link
                  href="https://www.digicert.com/blog/qualify-for-a-vmc-how-to-trademark-your-logo"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  this resource <ExportOutlined />.{' '}
                </Link>
                If you do not wish to trademark your logo, you&apos;ll need to consider a Common
                Mark Certificate.
              </p>
            </div>
          </motion.div>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 12 }} style={{ marginBottom: '24px' }}>
          <motion.div
            className="card testimonial-slider"
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="card-testimonial-slider-content">
              <div className="card-testimonial-slider-details mg-bottom-24px">
                <div className="text-400 medium color-neutral-100">
                  <Image
                    src="/images/icon-blue-check.svg"
                    loading="eager"
                    alt="Get Your Logo Ready"
                    height={32}
                    width={32}
                  />{' '}
                  Get Your Logo Ready
                </div>
                <div className="text-200 medium color-accent-1">
                  Optimize visuals for an impactful impression
                </div>
              </div>
              <p className="color-neutral-300">
                To ensure an optimal display in email inboxes, it&apos;s imperative that your logo
                is formatted in the SVG Portable/Secure (SVG-P/S) format. Please direct your design
                team to our dedicated{' '}
                <Link
                  href="https://www.digicert.com/blog/getting-ready-for-bimi-prep-your-logo"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  BIMI-ready logo guide <ExportOutlined />{' '}
                </Link>{' '}
                , so they can effectively align your logo with the exacting standards set by Brand
                Indicators for Message Identification.
              </p>
            </div>
          </motion.div>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 12 }} style={{ marginBottom: '24px' }}>
          <motion.div
            className="card testimonial-slider"
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="card-testimonial-slider-content">
              <div className="card-testimonial-slider-details mg-bottom-24px">
                <div className="text-400 medium color-neutral-100">
                  <Image
                    src="/images/icon-blue-check.svg"
                    loading="eager"
                    alt="Secure Your VMC Today"
                    height={32}
                    width={32}
                  />{' '}
                  Secure Your VMC Today
                </div>
                <div className="text-200 medium color-accent-1">
                  Elevate your brand&apos;s email presence with certification
                </div>
              </div>
              <p className="color-neutral-300">
                If you&apos;re prepared and ready for VMC, take the initiative and kick off the
                process by purchasing a Verified Mark Certificate right away. If your organization
                operates with multiple logos or has several domains, it&apos;s crucial to refer to
                our{' '}
                <Link
                  href="https://www.digicert.com/blog/how-many-vmcs-will-i-need"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  VMC quantity guide <ExportOutlined />
                </Link>
                . This will help you determine the precise number of VMCs you&apos;ll need to pull
                this off.
              </p>
            </div>
          </motion.div>
        </Col>
      </Row>

      <div className="hidden-on-desktop w-slider-nav w-round"></div>
    </div>
  );
}

export default VMCPrepare;
